/**
 * The notification panel's tabs, one entry each: which notifications belong
 * to it (match + the v2 query that pages it), its icon and words, and how a
 * notification becomes a row the panel renders. A new kind of notification
 * is one entry (or one more type in an entry's list) here; the panel and the
 * store need no change.
 *
 * Rows keep the shape the panel's message components have always read
 * (isLeave, isEquipment, orgData, onClick...), so the rendering is the same.
 */
import { differenceInMonths } from "date-fns"
import uniq from "lodash/uniq"
import flatMap from "lodash/flatMap"
import {
    PiAlarmDuotone,
    PiAirplaneDuotone,
    PiCalendarDotsDuotone,
    PiChatCircleDuotone,
    PiConfettiDuotone,
    PiFireDuotone,
    PiListChecksDuotone,
    PiReceiptDuotone,
    PiSealCheckDuotone,
    PiTractorDuotone,
} from "react-icons/pi"

import history, { addParams, goToParams } from "../History"
import { getObjectId, getRefId, isArchived, isGtGr } from "../../utils/utils"
import { isEquipmentNotification } from "../../utils/equipment-workflows"
import { isSignOffNotification } from "../../utils/signOffs"

const LEAVE_TYPES = [
    "leave_request_approval_final",
    "leave_request_approval",
    "leave_request_rejected",
    "leave_request_approved",
]
// Told by the system, not by a person: no people on the notification.
const SYSTEM_LEAVE_TYPES = ["leave_request_rejected", "leave_request_approved"]
// The Virtual PM's question about an overdue task, and what it tells the project manager.
const VIRTUAL_PM_TYPES = ["virtualpm_ask", "virtualpm_update"]
// Notifications that open a task (cr_decided: a change request the person emailed out was signed or declined).
const TASK_TYPES = ["task_assigned", "cr_decided"]
const WORK_TYPES = [...TASK_TYPES, "timesheet_overdue", ...VIRTUAL_PM_TYPES]
// The Virtual PM asks as mAX: nobody is on the notification.
const MAX_SENDER = { avatar: "/img/mAX.png", email: "help@missionx.ai", ref: { firstName: "mAX", lastName: " " } }

const youngerThan = (months) => (n) => !n.createdAt || differenceInMonths(new Date(), new Date(n.createdAt)) < months

/**
 * The org of a notification: populated by the list, an id when it came over
 * the socket - then the person's own copy of it.
 */
export const orgOf = (n, app) => {
    if (n?.org && typeof n.org === "object" && n.org.title !== undefined) return n.org

    const id = getObjectId(n?.org)

    return (app?.state?.orgs || []).find((o) => o._id === id) || (n?.org && typeof n.org === "object" ? n.org : null)
}

/**
 * @typedef {Object} RowContext
 * @property {Object} app
 * @property {function} close   closes the open thread, else the panel
 * @property {function} closePanel closes the panel
 * @property {function(String)} markRead
 */

export const NOTIFICATION_CATEGORIES = [
    {
        key: "pinned",
        title: "Pinned messages",
        tip: "Pinned and important messages",
        Icon: PiFireDuotone,
        badgeColor: "secondary",
        empty: "No news is good news I guess.",
        noMarkAll: true,
        query: { type: ["payment"] },
        match: (n) => n.type === "payment",
        toRows: (n, { app, close, markRead }) => {
            const org = orgOf(n, app)

            if (!org || !app.state.orgs.find((o) => o._id === getObjectId(org))) return []

            return [
                {
                    ...n,
                    originalNote: n,
                    isPinned: true,
                    system: true,
                    title: org.title,
                    orgData: org,
                    subject: "Payment issue",
                    onMouseEnter: () => markRead(n._id),
                    body: "Hello, your organization's payment information needs to be updated to continue your mission uninterrupted.",
                    onClick: () => {
                        markRead(n._id)
                        close()
                        addParams([
                            { param: "org", value: getObjectId(org) },
                            { param: "org-cfg", value: "1" },
                        ])
                    },
                },
            ]
        },
    },
    {
        key: "chats",
        title: "Chats",
        tip: "Task msgs",
        Icon: PiChatCircleDuotone,
        empty: "You will be able to follow people's replies to task threads here.",
        query: { type: ["mentionComment"] },
        match: (n) => n.type === "mentionComment" && n.data?.roomName === "ActionItem",
        toRows: (n) => [{ ...n, mission: {} }],
    },
    {
        key: "work",
        title: "My work",
        tip: "Tasks assigned to you, change requests signed, overdue task check-ins and timesheet reminders",
        Icon: PiListChecksDuotone,
        hideWhenEmpty: true,
        query: { type: WORK_TYPES },
        match: (n) => WORK_TYPES.includes(n.type),
        keep: youngerThan(3),
        toRows: (n, { app, close, markRead }) => [
            {
                ...n,
                ...(VIRTUAL_PM_TYPES.includes(n.type) && !n.people?.length ? { people: [MAX_SENDER] } : {}),
                orgData: orgOf(n, app),
                title: n.title,
                isWork: true,
                onClick: () => {
                    markRead(n._id)
                    close()

                    if (VIRTUAL_PM_TYPES.includes(n.type)) {
                        addParams([
                            { param: "follow-up-org", value: getObjectId(n.org) },
                            { param: "follow-up", value: n.data?.followUpId || n.typeId },
                        ])
                    } else if (TASK_TYPES.includes(n.type)) {
                        addParams([{ param: "action-item", value: n.data?.actionItem || n.typeId }])
                    } else {
                        history.push("/home")
                        app.setState({ timeTracking: true })
                    }
                },
            },
        ],
    },
    {
        key: "reminders",
        title: "Reminders",
        tip: "Reminders and alerts you set",
        Icon: PiAlarmDuotone,
        hideWhenEmpty: true,
        query: { type: ["reminder"] },
        match: (n) => n.type === "reminder",
        keep: youngerThan(3),
        toRows: (n, { app, close, markRead }) => [
            {
                ...n,
                orgData: orgOf(n, app),
                title: n.title,
                isWork: true,
                onClick: () => {
                    markRead(n._id)
                    close()

                    const targetType = n.data?.targetType
                    const targetId = n.data?.targetId

                    if (targetType === "task") {
                        addParams([{ param: "action-item", value: targetId }])
                    } else if (targetType === "mission") {
                        addParams([{ param: "mission", value: targetId }])
                    } else if (targetType === "signOff") {
                        addParams([
                            { param: "sign-off-org", value: getObjectId(n.org) },
                            { param: "sign-off", value: targetId },
                        ])
                    } else {
                        app.setState({ showReminders: true })
                    }
                },
            },
        ],
    },
    {
        key: "invites",
        title: "Invites",
        tip: "Invites to projects and organizations",
        Icon: PiConfettiDuotone,
        query: { type: ["invite", "orgInvite"] },
        match: (n) => n.type === "invite" || n.type === "orgInvite",
        keep: youngerThan(4),
        toRows: (n, { markRead }) => [
            {
                ...n,
                isInvite: true,
                title: "Welcome to the " + (n.type === "orgInvite" ? "organization" : "mission"),
                subject: n.type,
                body: n.body,
                onClick: () => {
                    markRead(n._id)
                    history.push("/home?invite-notice=" + n._id)
                },
            },
        ],
    },
    {
        key: "leaves",
        title: "Leave notifications",
        tip: "Leave requests",
        Icon: PiAirplaneDuotone,
        query: { type: LEAVE_TYPES },
        match: (n) => LEAVE_TYPES.includes(n.type),
        keep: (n) => youngerThan(6)(n) && (!!n.people?.[0] || SYSTEM_LEAVE_TYPES.includes(n.type)),
        toRows: (n, { app, closePanel, markRead }) => [
            {
                ...n,
                orgData: orgOf(n, app),
                title: n.title,
                isLeave: true,
                onClick: () => {
                    markRead(n._id)

                    // Approved: the person asked, so their own leaves are the place.
                    if (n.type === "leave_request_approved") {
                        history.push("/board?board-tab=leaves")
                        return
                    }

                    const org = app.state.orgs.find((o) => o._id === getObjectId(n.org))

                    if (!org) return

                    const meInOrg = org.people.find((p) => getRefId(p) === app.state.person._id)

                    if (meInOrg?.canApproveLeaves || meInOrg?.canManagePeople || meInOrg?.permission === 2) {
                        closePanel()
                        goToParams({
                            add: [
                                { param: "org", value: org._id },
                                { param: "org-tab", value: "org-leaves" },
                            ],
                        })
                    } else {
                        history.push("/board?board-tab=leaves")
                    }
                },
            },
        ],
    },
    {
        key: "invoices",
        title: "Vendor invoices",
        tip: "Vendor invoices awaiting approval",
        Icon: PiReceiptDuotone,
        query: { type: ["vendor_invoice_approval"] },
        match: (n) => n.type === "vendor_invoice_approval",
        keep: youngerThan(6),
        toRows: (n, { app, close, markRead }) => [
            {
                ...n,
                orgData: orgOf(n, app),
                title: n.title,
                isVendorInvoice: true,
                onClick: () => {
                    markRead(n._id)
                    close()
                    goToParams({
                        add: [
                            { param: "org", value: getObjectId(n.org) },
                            { param: "org-tab", value: "org-vendors" },
                            { param: "vendors-tab", value: "vendor-invoices" },
                        ],
                    })
                },
            },
        ],
    },
    {
        key: "signOffs",
        title: "Sign-offs",
        tip: "Sign-offs",
        Icon: PiSealCheckDuotone,
        hideWhenEmpty: true,
        query: { typePrefix: ["signoff_"] },
        match: (n) => isSignOffNotification(n.type),
        keep: youngerThan(6),
        toRows: (n, { app, close, markRead }) => [
            {
                ...n,
                orgData: orgOf(n, app),
                title: n.title,
                isSignOff: true,
                onClick: () => {
                    markRead(n._id)
                    close()
                    addParams([
                        // Not `org`: that would open the org screen under the sign-off
                        { param: "sign-off-org", value: getObjectId(n.org) },
                        { param: "sign-off", value: n.typeId },
                    ])
                },
            },
        ],
    },
    {
        key: "equipment",
        title: "Equipment",
        tip: "Equipment",
        Icon: PiTractorDuotone,
        hideWhenEmpty: true,
        query: { typePrefix: ["equipment_"] },
        match: (n) => isEquipmentNotification(n.type),
        keep: youngerThan(6),
        toRows: (n, { app, close, markRead }) => {
            const isBooking = ["equipment_booking_approval", "equipment_booking_decision"].includes(n.type)

            return [
                {
                    ...n,
                    orgData: orgOf(n, app),
                    title: n.title,
                    isEquipment: true,
                    onClick: () => {
                        markRead(n._id)
                        close()
                        goToParams({
                            add: [
                                { param: "org", value: getObjectId(n.org) },
                                { param: "org-tab", value: "org-equipment" },
                                { param: "equipment-tab", value: isBooking ? "equipment-scheduling" : "equipment-list" },
                                ...(n.data?.equipment && !isBooking
                                    ? [{ param: "equipment", value: n.data.equipment }]
                                    : []),
                            ],
                        })
                    },
                },
            ]
        },
    },
    {
        key: "allocations",
        title: "Allocations",
        tip: "Allocation changes",
        Icon: PiCalendarDotsDuotone,
        visible: (app) => !!app.state.orgs.find((o) => isGtGr(o)),
        query: { type: ["orgRolePlanItemStatusChange"] },
        match: (n) => n.type === "orgRolePlanItemStatusChange",
        toRows: (n, { app }) => {
            const org = app.state.orgs.find((o) => o._id === getObjectId(n.org))

            if (!org) return []

            return Object.keys(n.data?.statusMap || {}).map((key) => {
                const obj = n.data.statusMap[key]
                const sum = (list) => (list || []).reduce((total, item) => total + item.hours, 0)
                const oldHours = sum(obj.oldWeeklyLumpHours) + sum(obj.oldDailyHours)
                const hours = sum(obj.weeklyLumpHours) + sum(obj.dailyHours)
                const program = org.programs?.find((p) => p._id === obj.programId)
                const roleName = org.roles?.find((r) => getObjectId(r) === obj.roleId)?.name

                return {
                    ...n,
                    _rowKey: `${n._id}-${key}`,
                    isAllocation: true,
                    title: orgOf(n, app)?.title || org.title,
                    orgData: orgOf(n, app) || org,
                    subject: program?.title,
                    body: `${roleName || "Role not found"} ${hours}/hrs (Delta: ${
                        hours - oldHours
                    }/hrs) Status changed to: ${obj.status}`,
                }
            })
        },
    },
]

export const CATEGORY_BY_KEY = Object.fromEntries(NOTIFICATION_CATEGORIES.map((c) => [c.key, c]))

/**
 * GT orgs: client accounts not linked to a group, for the people who link them.
 */
export const checkForClientPrograms = (app) => {
    const orgData = app.state.orgs.find((o) => isGtGr(o, "prod"))

    let orphans = 0

    if (orgData) {
        const meInOrg = orgData.people?.find((p) => getRefId(p) === getRefId(app.state.person))

        if (meInOrg?.canApproveRoles || meInOrg?.permission === 2) {
            const allClientsUsed = uniq(
                flatMap(
                    orgData.programs?.filter((p) => !isArchived(p) && !p.portfolio),
                    "clients",
                ).filter((c) => !!c),
            )
            orphans = orgData.clients?.length - allClientsUsed?.length
        }
    }

    return { orgData, orphans }
}

/**
 * The pins that are not notifications: what the app itself wants the person
 * to act on.
 */
export function systemPins({ app, close }) {
    const pins = []
    const orphanClients = checkForClientPrograms(app)

    if (orphanClients?.orphans) {
        pins.push({
            _rowKey: "pin-orphan-clients",
            isPinned: true,
            system: true,
            orgData: orphanClients.orgData,
            title: orphanClients.orgData.title,
            subject: "Action required",
            body: `There are ${orphanClients.orphans} client accounts not linked to a group. Please link these from the resource allocation screens.`,
        })
    }

    if (app.state.missingTimesheets > 0) {
        pins.push({
            _rowKey: "pin-missing-timesheets",
            isPinned: true,
            system: true,
            title: "A message from mAX",
            orgData: { title: "missionX", logo: "/img/mAX.png" },
            subject: "Missing timesheets 🔥",
            body: `It looks like
                ${app.state.missingTimesheets === 1 ? "there is " : "there are"}
                ${app.state.missingTimesheets} missing project timesheet(s)
                over a three week period.`,
            onClick: () => {
                close()
                history.push("/home")
                app.setState({ timeTracking: true })
            },
        })
    }

    return pins
}

/**
 * Every loaded notification as panel rows, by category key, newest first.
 *
 * @param list {Array<Object>} the store's list
 * @param ctx {RowContext}
 * @return {Object<String, Array<Object>>}
 */
export function buildRows(list, ctx) {
    const rows = Object.fromEntries(NOTIFICATION_CATEGORIES.map((c) => [c.key, []]))

    list.forEach((n) => {
        const category = NOTIFICATION_CATEGORIES.find((c) => c.match(n))

        if (!category || (category.keep && !category.keep(n))) return

        rows[category.key].push(...category.toRows(n, ctx))
    })

    rows.pinned = [...systemPins(ctx), ...rows.pinned]

    Object.keys(rows).forEach((key) =>
        rows[key].sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()),
    )

    return rows
}

/** Whether a tab shows in the bar. */
export const isCategoryVisible = (category, app, rows = []) =>
    (!category.visible || category.visible(app)) && (!category.hideWhenEmpty || rows.length > 0)
